import type {
  ConversationContext,
  ConversationParticipant,
} from '@stripe-internal/conversations-client';
import type {ApiMethods, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';

export type GetConversationParams = {
  conversationId: string;
  participant: ConversationParticipant;
  context?: ConversationContext;
};

export type GetConversationData = ReturnType<
  typeof FrameworkAdapter.fromApiConversation
>;

export type GetConversationResponse =
  | {code: number; data: GetConversationData}
  | {code: number; error: string};

type GetConversationDeps = {
  getConversation: ApiMethods['getConversation'];
  attachmentDownloadPath?: string;
  reportWarning?: ReportWarning;
};

export class GetConversationHandler {
  private deps: GetConversationDeps;

  constructor(deps: GetConversationDeps) {
    this.deps = deps;
  }

  call = async ({
    conversationId,
    participant,
    context,
  }: GetConversationParams): Promise<GetConversationResponse> => {
    if (!conversationId) return {code: 400, error: 'Missing conversationId'};
    const response = await this.deps.getConversation(conversationId);
    if (response.type === 'error')
      return FrameworkAdapter.toClientErrorResponse(response);
    if (
      response.data.id !== conversationId ||
      response.data.overview?.id !== conversationId
    )
      return {
        code: 500,
        error: 'Conversation response ID mismatch or missing overview',
      };
    return {
      code: 200,
      data: FrameworkAdapter.fromApiConversation({
        apiConversation: response.data,
        context,
        user: participant,
        isActive: response.data.overview.is_active,
        attachmentDownloadPath: this.deps.attachmentDownloadPath,
        reportWarning: this.deps.reportWarning,
      }),
    };
  };
}
